import { generatePath } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';

import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { StarsWeb } from '@swf/core/types/Window';
import {
  ClubMemberAvatars,
  Image,
  Loader,
  PKR_ROLE_TRANSLATIONS,
  useCheckUserClubRole,
  useGetCurrentClub,
  useHomeGamesWidget
} from '@swf/poker';

import { ClubRoute, ModalRoutes, UserClubRole } from '../../../types/common';
import type { MenuOption } from '../../DropdownMenu/DropdownMenu';
import Menu from '../../DropdownMenu/components/Menu/Menu';
import { MemberStatusesPerPage } from '../Members/utils';
import useGetClubPassword from '../hooks/useGetClubPassword';

import ClubTypeIcon from './ClubTypeIcon/ClubTypeIcon';
import CreateGameButtons from './CreateGameButtons/CreateGameButtons';
import {
  TXTCLI_Please_use_our_customer_suppor_ELL,
  TXTCLI_Report_Club,
  TXTCLI_Settings,
  TXTWCL_Share_Club
} from './Header.messages.json';
import styles from './Header.scss';
import SettingsButton from './SettingsButton/SettingsButton';
import ShareButton from './ShareButton/ShareButton';

export const options: MenuOption[] = [
  {
    text: <Translation {...TXTCLI_Settings} />,
    value: ClubRoute.Settings
  },
  {
    text: (
      <div>
        <p className={styles.reportClub}>
          <Translation {...TXTCLI_Report_Club} />
        </p>
        <p className={styles.reportClubInfo}>
          <Translation {...TXTCLI_Please_use_our_customer_suppor_ELL} />
        </p>
      </div>
    ),
    value: ClubRoute.ReportClub
  }
];

const Header: React.FC = () => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const club = useGetCurrentClub(clubId);
  const { push } = useHistory();
  const { clubPassword } = useGetClubPassword(Number(clubId));
  const { modal } = useDispatch<Dispatch>();
  const { widgetId } = useWidget();
  const [isOpen, setIsOpen] = useState(false);
  const roleForUser = club?.roleForUser;
  const clubName = club?.clubName;
  const clubImage = club?.clubImage;

  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();

  const isMember = useCheckUserClubRole(clubId, [UserClubRole.Member]);
  const isAdminOrManager = useCheckUserClubRole(clubId, [UserClubRole.Admin, UserClubRole.Manager]);

  const { clubNavHidden } = useSelector(widgetSelect.uiLayout);
  const isMobile = useSelector(select.device.isMobile);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const aliases = useSelector(select.cms.getAliases);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const enableMembers = isAdminOrManager || isMember;

  const clubOptionsById = useSelector(widgetSelect.clubOptionsById);
  const isOpenClub = clubOptionsById[clubId]?.open;

  useEffect(() => {
    if (!Object.keys(clubOptionsById).includes(clubId)) {
      widgetDispatch.clubOptions({ clubId: Number(clubId) });
    }
  }, [clubId, widgetDispatch, clubOptionsById]);

  const loadingState = useSelector(widgetSelect.loading);
  const isClubTypeiconLoading = loadingState[`clubOptionsById-${clubId}`];

  const onMenuTap = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => (e: any) => {
      switch (e.detail) {
        case 'settings':
          setIsOpen((prevValue) => !prevValue);
          break;
        case 'share':
          modal.enqueue({
            path: ModalRoutes.createClubSuccess,
            locationState: {
              clubId: Number(clubId),
              invitationCode: clubPassword,
              ignoreDataFetch: true,
              title: <Translation {...TXTWCL_Share_Club} />,
              widgetId
            }
          });
          break;
      }
    },
    [modal, clubId, clubPassword, widgetId]
  );

  useEffect(() => {
    if (!isAdminOrManager) return;
    widgetDispatch.fetchClubMembers({
      clubId,
      statuses: MemberStatusesPerPage.Pending,
      memberType: 'pending'
    });
  }, [clubId, isAdminOrManager, widgetDispatch]);

  useEffect(() => {
    if (isEmbedded) {
      window.starsweb?.homegames?.menuItemsVisibility?.(
        isAdminOrManager ? 'share|settings' : 'settings'
      );
    }
  }, [isEmbedded, isAdminOrManager]);

  useEffect(() => {
    document.addEventListener('starsmenutap', onMenuTap);

    return () => document.removeEventListener('starsmenutap', onMenuTap);
  }, [onMenuTap]);

  const handleSelect = ({ value }: MenuOption) => {
    setIsOpen(false);
    switch (value) {
      case ClubRoute.Settings:
        if (aliases) {
          push(
            generatePath(aliases.homeGamesClubs as string, {
              clubId,
              section: ClubRoute.Settings
            })
          );
        }
        break;
      case ClubRoute.ReportClub:
        if (isEmbedded) {
          const starsweb = window.starsweb as StarsWeb;
          starsweb?.app.execute?.('support');
        } else {
          if (aliases) {
            window.open(generatePath(aliases.helpCenter as string, {}), '_blank');
          }
        }
        break;
      default:
        break;
    }
  };

  const menuContainerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuContainerRef, () => {
    setIsOpen(false);
  });

  return (
    <div
      className={classNames(styles.container, {
        [styles.hidden]: clubNavHidden
      })}
    >
      {isOpen && (
        <div ref={menuContainerRef}>
          <Menu menuClassName={styles.menu} options={options} onSelect={handleSelect} />
        </div>
      )}
      <Loader loading={!club}>
        <div
          className={classNames(styles.innerContainer, {
            [styles.innerContainerEmbedded]: isMobileEmbedded
          })}
        >
          <div className={styles.avatarNameContainer}>
            <div className={styles.avatar}>
              <Image imageByteArray={clubImage} />
            </div>
            <div className={styles.nameContainer}>
              <div className={styles.nameRow}>
                <div className={styles.name}>{clubName}</div>
                <div className={styles.clubTypeIconWrapper}>
                  <ClubTypeIcon isOpenClub={isOpenClub} loading={isClubTypeiconLoading} />
                </div>
              </div>
              <Translation tagName="span" {...PKR_ROLE_TRANSLATIONS?.[roleForUser ?? '']} />
            </div>
          </div>
          {!isMobileEmbedded && (
            <div className={classNames(styles.actions, { [styles.actionsMobile]: isMobile })}>
              {enableMembers && (
                <ClubMemberAvatars
                  avatarClassName={styles.memberAvatar}
                  clubId={Number(clubId)}
                  className={styles.avatars}
                />
              )}
              {isAdminOrManager && (
                <>
                  <ShareButton />
                  <CreateGameButtons />
                </>
              )}
            </div>
          )}
          {isMobileEmbedded && <CreateGameButtons />}
          {!isMobileEmbedded && <SettingsButton onSelect={handleSelect} />}
        </div>
      </Loader>
    </div>
  );
};

export default Header;
